import axios from 'axios';
import { initAxios } from 'utils/axiosHelper';

// Import Hub "Database Rules" SOP extraction is served by PyAPI at
// /pyapi/import-hub/sop-rules. initAxios attaches the Keycloak bearer token,
// which PyAPI verifies against the request's group_id.
// eslint-disable-next-line no-undef
const PYAPI_BASE_URL = process.env.REACT_APP_PYAPI_URL || '/pyapi';
const apiClient = initAxios(PYAPI_BASE_URL);

// The upload request only stores the file and starts a background job, so it
// returns quickly even for large SOPs; extraction itself is polled.
const UPLOAD_TIMEOUT_MS = 60 * 1000;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 20 * 60 * 1000;

export type SopRuleActionType =
  | 'enable_fta'
  | 'disable_fta'
  | 'enable_exclusion'
  | 'disable_exclusion'
  | 'none';

export interface SopRuleCondition {
  field: 'country_of_origin' | 'hts_code' | 'category';
  operator: 'in' | 'not_in' | 'starts_with';
  values: string[];
}

export interface SopRule {
  id: string;
  title: string;
  detail: string;
  applicable: boolean;
  /** Why BITE can't apply the rule; empty for applicable rules. */
  reason: string;
  action: { type: SopRuleActionType; code: string; code_label: string };
  /** Empty means every product in the database. */
  conditions: SopRuleCondition[];
  section: string;
  page: number | null;
  quote: string;
}

export type SopExtractionStage =
  | 'reading'
  | 'finding_sections'
  | 'extracting'
  | 'verifying'
  | 'done';

export interface SopExtraction {
  extraction_id: string;
  status: 'processing' | 'complete' | 'failed';
  stage: SopExtractionStage;
  filename: string;
  database_id: string;
  progress: { chunks_done: number; chunks_total: number };
  result: {
    rules: SopRule[];
    page_count: number | null;
    model: string;
    warnings: string[];
  } | null;
  error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : fallback;
  }
  const data = error.response?.data as any;
  const upstreamMessage =
    (typeof data?.message === 'string' && data.message) ||
    (typeof data?.error === 'string' && data.error) ||
    (typeof data?.detail === 'string' && data.detail) ||
    '';
  return upstreamMessage || fallback;
};

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

export const isAbortError = (error: unknown): boolean =>
  axios.isCancel(error) ||
  (error instanceof DOMException && error.name === 'AbortError');

export class SopRulesAPI {
  static async createExtraction(
    file: File,
    groupId: string,
    databaseId: string,
    signal?: AbortSignal,
  ): Promise<SopExtraction> {
    try {
      const formData = new FormData();
      formData.append('group_id', groupId);
      formData.append('database_id', databaseId);
      formData.append('file', file);
      const response = await apiClient.post(
        '/import-hub/sop-rules/extractions',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: UPLOAD_TIMEOUT_MS,
          signal: signal,
        },
      );
      return response.data;
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw new Error(getErrorMessage(error, 'Failed to upload the SOP.'));
    }
  }

  static async getExtraction(
    extractionId: string,
    groupId: string,
    signal?: AbortSignal,
  ): Promise<SopExtraction> {
    try {
      const response = await apiClient.get(
        `/import-hub/sop-rules/extractions/${encodeURIComponent(extractionId)}`,
        { params: { group_id: groupId }, signal: signal },
      );
      return response.data;
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw new Error(
        getErrorMessage(error, 'Failed to load extraction status.'),
      );
    }
  }

  /**
   * Poll until the extraction completes and return the finished record.
   * `onUpdate` receives every in-progress record so the caller can show the
   * current stage. Rejects with the job's own error message when it fails,
   * and with an AbortError when `signal` aborts.
   */
  static async pollExtraction(
    extractionId: string,
    groupId: string,
    options: {
      signal?: AbortSignal;
      onUpdate?: (extraction: SopExtraction) => void;
    } = {},
  ): Promise<SopExtraction> {
    const startedAt = Date.now();
    for (;;) {
      await wait(POLL_INTERVAL_MS, options.signal);
      const record = await SopRulesAPI.getExtraction(
        extractionId,
        groupId,
        options.signal,
      );
      if (record.status === 'failed') {
        throw new Error(
          record.error || 'Rule extraction failed. Please try again.',
        );
      }
      if (record.status === 'complete') {
        return record;
      }
      options.onUpdate?.(record);
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        throw new Error(
          'Reading this SOP is taking longer than expected. Please try again shortly.',
        );
      }
    }
  }
}

export default SopRulesAPI;
